import { AxiosResponse } from 'axios';
import { CartResponseType, IModifier, ModifierGroup, ModifierOptions, OrderSource, OrderType } from '@zeus/shared';
import { deleteCart, getCart, updateCart } from '../../services/cart/Cart';
import { Cart, CartItem, NearbyRestaurant, UpdateCartItemModel } from '../../app/(routes)/online-ordering/types';
import { prepareCart } from '../../services/restuarant/Restaurant';
import { createPrepareCartPayload, getOrderingTimeById, sendEventToPostHog } from '../../utils/UtilFunctions';
import { deleteCookie, getCookie } from 'cookies-next';
import { defaultCartExpiryTime } from '../../config/constants';
import { store } from '@store/online/index';
import { updateCartSettings, updateOrderingTime } from '@store/online/cart/cartSlice';
import { cloneDeep } from 'lodash';
import { decryptData } from '../../utils/Crypto';
import { EventType, EventAction } from '../../config/GAConstants';

export function normalizeLoyaltyApplied(items: any[]): any[] {
  const appliedPerItem = new Set<number>();
  return items.map((item) => {
    if (item.isLoyaltyApplied !== true) {
      return item;
    }

    if (appliedPerItem.has(item.id)) {
      return {
        ...item,
        isLoyaltyApplied: false,
        loyaltyAmountApplied: null,
        loyaltyId: null,
        loyaltyName: null,
        loyaltyTier: null,
      };
    }

    appliedPerItem.add(item.id);

    return item;
  });
}

export const convertDateFormat = (inputDate: string) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const [, month, dayNumber] = inputDate.split(' ');
  const monthIndex = months.indexOf(month);
  const year = new Date().getFullYear();
  const formattedDate = new Date(year, monthIndex, parseInt(dayNumber, 10));

  return `${formattedDate.getFullYear()}-${(formattedDate.getMonth() + 1).toString().padStart(2, '0')}-${formattedDate
    .getDate()
    .toString()
    .padStart(2, '0')}`;
};

export const getCartData = async (
  phoneOrSession: string,
  restaurantId: number,
  type: string,
  orderType: string,
  orderSource: string,
  guestSessionId?: string
) => {
  const response: Promise<CartResponseType> = await getCart(
    phoneOrSession,
    restaurantId,
    type,
    orderType,
    orderSource,
    guestSessionId
  );
  return response;
};

export const saveCartData = async (cart: Cart) => {
  const isQr = store.getState().restaurant.isQr;
  const isScheduledOrder = getCookie('scheduleOrder');
  const scheduledDateTime = getCookie('scheduledDate');
  const prepareCartPayload = createPrepareCartPayload(cart, isQr);
  const response: CartResponseType = await prepareCart({
    ...prepareCartPayload,
    isScheduledOrder: isScheduledOrder === 'true' ? true : false,
    scheduledDateTime,
  });
  store.dispatch(updateCartSettings({ cartId: response?.data?.cart.id }));
  sendEventToPostHog(EventType.EVENT, EventAction.OL_PREPARE_CART, { prepareCart: response?.data });
  return response;
};

export const deleteCartItem = async (id: number, restaurantId: number) => {
  if (id) {
    const response: AxiosResponse<CartResponseType> = await deleteCart(id, restaurantId);
    return response;
  } else return null;
};

export const updateCartById = async (item: UpdateCartItemModel) => {
  try {
    if (item && item.id) {
      const response: AxiosResponse<CartResponseType> | null = await updateCart(item, item.id);
      return response;
    } else return null;
  } catch (error) {
    console.log(error, 'error from updateCartById ');
    return null;
  }
};

export const getLocalStorageData = () => {
  const businessId = store.getState().restaurant.selectedBusinessId;
  const selectedRestaurantAddress = store.getState().restaurant.selectedRestaurantAddressNew;
  const address = getRestaurantAddress(selectedRestaurantAddress, businessId);
  const activeRestaurant = store.getState()?.restaurant?.activeRestaurant ?? null;
  const user = store.getState()?.auth?.user;
  const authUserAddress = user?.address || {};
  return {
    customerUsername: decryptData(user.username || ''),
    businessId,
    resturantAddress: address,
    deliveryDate: store.getState().restaurant.selectedPickupDeliveryDate || '',
    orderType: store.getState().restaurant.orderType,
    estimated_time_slot: getOrderingTimeById()?.orderingTime || {},
    isGuest: store.getState().auth.isGuest,
    userAddress: decryptData(authUserAddress?.userAddress || ''),
    appartmentAddress: decryptData(authUserAddress?.userAppartmentAddress || ''),
    city: decryptData(authUserAddress?.city || ''),
    state: decryptData(authUserAddress?.state || ''),
    zipcode: Number(decryptData(authUserAddress?.zipcode || '')),
    street: decryptData(authUserAddress?.street || ''),
    userLatitude: Number(decryptData(store.getState().auth?.user?.userLatitude || 0)),
    userLongitude: Number(decryptData(store.getState().auth?.user?.userLongitude || 0)),
    firstName: decryptData(user.fName || ''),
    lastName: decryptData(user.lName || ''),
    email: decryptData(user.email || ''),
    guestSessionId: store.getState().auth.guestSessionId,
    tableNo: store.getState().restaurant.tableNo,
    isQr: store.getState().restaurant.isQr || false,
    activeRestaurant,
  };
};

const getRestaurantAddress = (selectedRestaurantAddress: NearbyRestaurant | any, businessId: number | null) => {
  if (
    selectedRestaurantAddress &&
    businessId &&
    selectedRestaurantAddress[businessId] &&
    selectedRestaurantAddress[businessId].restaurant
  ) {
    return selectedRestaurantAddress[businessId].restaurant;
  }
  return {};
};

export const syncCartItems = async (state: Cart | undefined) => {
  const localData = getLocalStorageData();
  const businessId = localData?.businessId;
  const restaurantAddress = localData?.resturantAddress || {};
  const orderType = store.getState()?.restaurant?.orderType || OrderType.Takeaway;
  const isQr = store.getState().restaurant.isQr;
  const orderSource = isQr ? OrderSource.QR : OrderSource.Website;
  const deliveryDate = localData?.['deliveryDate'];
  const pickupDelDate = deliveryDate ? convertDateFormat(deliveryDate) : '';

  const localCartData = businessId ? cloneDeep(store.getState()?.cart?.orderingTime?.[businessId]?.cart) : null;
  const isCartStale =
    localCartData?.lastAdded && new Date(localCartData.lastAdded).getTime() < Date.now() - defaultCartExpiryTime;

  let localCart: Partial<Cart> = { items: [] };

  if (isCartStale) {
    await saveCartLocal({
      items: [],
      actualPrice: 0,
      discountedPrice: 0,
      orderedQuantity: 0,
      pointsUsed: 0,
      totalBill: 0,
      quantity: 0,
    });
  } else if (localCartData) {
    localCart = localCartData || { items: [] };
  }

  let loginCart: Partial<Cart> | null = null;
  const shouldSyncItems = state?.itemSync !== false;

  if (shouldSyncItems && localData?.guestSessionId) {
    loginCart =
      state?.cartType === 'remote'
        ? await getLoginCartData(
            restaurantAddress.id,
            localData.customerUsername,
            localData.guestSessionId,
            orderType || state?.cart?.order_type || OrderType.Takeaway,
            state?.cart?.orderSource || orderSource || OrderSource.Website
          )
        : localCart;
  }

  const accountId = state?.accountId?.length ? state.accountId : loginCart?.restaurant?.accountId || '';

  const baseCart: Cart | any = {
    ...(state || {}),
    ...loginCart,
    accountId,
    customerUsername: localData?.customerUsername,
    restaurantId: restaurantAddress.id,
    restaurantName: restaurantAddress.name || '',
    resturantAddress: restaurantAddress,
    deliveryPickupDate: pickupDelDate,
    order_type: localData?.orderType,
    customer: {
      firstName: localData?.firstName,
      lastName: localData?.lastName,
      emailAddress: localData?.email,
    },
    address: localData?.userAddress || '',
    apartment: String(localData?.appartmentAddress || ''),
    city: localData?.city || '',
    state: localData?.state || '',
    street: localData?.street || '',
    zipCode: localData?.zipcode || 0,
    latitude: localData?.userLatitude || 0,
    longitude: localData?.userLongitude || 0,
    guestSessionId: localData?.guestSessionId || '',
    tableNo: localData?.tableNo || null,
    ...(localCart?.pointsUsed && { pointsUsed: localCart.pointsUsed }),
    activeRestaurant: localData?.activeRestaurant || null,
  };

  if (state?.cartType === 'remote' && localCart?.giftCard) {
    baseCart.giftCard = localCart?.giftCard;
  }
  if (!localData?.isQr) {
    const orderingTime = getOrderingTimeById()?.orderingTime;
    baseCart.estimatedTimeSlot = orderingTime;
    baseCart.estimatedTimeSlotId = orderingTime?.id || 0;
  }

  delete baseCart.itemSync;
  return baseCart;
};

export const getLoginCartData = async (
  restaurantId: number,
  phoneNumber: string,
  guestSessionId: string,
  orderType: string,
  orderSource: string
) => {
  if (restaurantId) {
    try {
      let cartData = null;
      if (phoneNumber) {
        cartData =
          (await getCartData(phoneNumber, restaurantId, 'phone', orderType, orderSource, guestSessionId)).data?.cart ||
          null;
      } else if (guestSessionId) {
        cartData =
          (await getCartData(guestSessionId, restaurantId, 'session', orderType, orderSource)).data?.cart || null;
      }

      const restaurantCustomer = (cartData?.customer?.restaurantCustomers || []).filter(
        (record) => record.restaurantId === restaurantId
      );
      return { ...cartData, userPoints: restaurantCustomer.length ? restaurantCustomer[0]?.userPoints : 0 };
    } catch (error) {
      console.error('Error fetching cart data:', error);
    }
  }
  return {};
};

export const getLocalCartData = (phoneNumber: string, isGuest = false) => {
  if (isGuest || !phoneNumber) {
    const cartItem = store.getState()?.cart?.userCartItem || {};

    if (cartItem && Object.keys(cartItem).length) {
      const businessId = store.getState().restaurant.selectedBusinessId;

      const cartBusinessId = cartItem?.businessId;

      if (businessId != cartBusinessId) return {};
    }

    const totalBill = cartItem?.totalBill || 0;
    const actualPrice = cartItem?.actualPrice || 0;
    const discountedPrice = cartItem?.discountedPrice || 0;
    const quantity = cartItem?.quantity || 0;
    const orderedQuantity = cartItem?.orderedQuantity || 0;
    const deliveryFeeInDollars = cartItem?.deliveryFeeInDollars || 0;
    const platformFee = cartItem?.platformFee || 0;
    const tipAmount = cartItem?.tipAmount || 0;
    const tipPercentage = cartItem?.tipPercentage || 0;
    const totalTax = cartItem?.totalTax || 0;

    return {
      items: cartItem?.items || [],
      totalBill,
      actualPrice,
      discountedPrice,
      quantity,
      orderedQuantity,
      deliveryFeeInDollars,
      platformFee,
      tipAmount,
      tipPercentage,
      totalTax,
    };
  }
  return {};
};

const handleModifier = (
  modifier: IModifier,
  price: number,
  itemDiscountedPrice: number,
  cartQuantity: number,
  isTopLevelModifier: boolean,
  isNestedModifier = false
): { netPriceSoFar: string } => {
  if (
    modifier &&
    modifier.price &&
    modifier.nestedModifiers &&
    modifier.nestedModifiers.length === 0 &&
    !isTopLevelModifier &&
    !isNestedModifier
  ) {
    price += Number(modifier.price) * (modifier.orderedQuantity ?? 1);
  }
  if (modifier && modifier.nestedModifiers) {
    for (const nestedmodifier of modifier.nestedModifiers) {
      let isNested = false;
      if (nestedmodifier.modifierOption && nestedmodifier.modifierOption.length > 0) isNested = true;
      const { netPriceSoFar } = handleModifier(
        nestedmodifier,
        price,
        itemDiscountedPrice,
        cartQuantity,
        false,
        isNested
      );
      price = Number(netPriceSoFar);
    }
  }
  if (modifier && modifier.modifierOption) {
    for (const modifierOption of modifier.modifierOption) {
      const { netPriceSoFar } = handleModifierOptions(modifierOption, price);
      price = Number(netPriceSoFar);
    }
  }

  return { netPriceSoFar: price.toFixed(2) };
};

const handleModifierOptions = (
  modifierOption: IModifier | ModifierOptions,
  price: number
): { netPriceSoFar: string } => {
  if (modifierOption && modifierOption.price) {
    price += Number(modifierOption.price) * (modifierOption.orderedQuantity ?? 1);
  }
  return { netPriceSoFar: price.toFixed(2) };
};
export const handleModifierGroups = (
  modifierGroup: ModifierGroup,
  price: number,
  itemDiscountedPrice: number,
  cartQuantity: number
): { netPriceSoFar: string } => {
  if (modifierGroup.modifiers && modifierGroup.modifiers.length > 0) {
    for (const modifier of modifierGroup.modifiers) {
      const { netPriceSoFar } = handleModifier(modifier, price, itemDiscountedPrice, Number(cartQuantity), true);
      price = Number(netPriceSoFar);
    }
  }
  return { netPriceSoFar: price.toFixed(2) };
};

export const calculatePrice = (data: CartItem) => {
  let price = 0;
  const itemDiscountedPrice = 0;
  if (data.selectedModifierGroups && data.selectedModifierGroups.length > 0) {
    for (const modifierGroup of data.selectedModifierGroups) {
      const { netPriceSoFar } = handleModifierGroups(
        modifierGroup,
        price,
        itemDiscountedPrice,
        Number(data.orderedQuantity)
      );
      price = Number(netPriceSoFar);
    }
  }

  return price;
};

export const setCartMetadata = (cart: Cart | any) => {
  let quantityFound = false;
  if ((cart && cart?.quantity) || cart?.quantity == 0) {
    quantityFound = true;
    cart['orderedQuantity'] = cart.quantity;
  }

  const items: CartItem[] = cart?.items || [];
  cart['discountedPrice'] = items.reduce((sum, item) => sum + Number(item?.discountedPrice ?? 0), 0) ?? 0;

  if (cart && cart.items) {
    for (const [index, item] of cart.items.entries()) {
      const cartItemId = item['id'];

      if (!quantityFound) cart['orderedQuantity'] += cart.orderedQuantity || cart.quantity || 0;
      let updatedItem = { ...item };

      if (updatedItem.item) {
        updatedItem = {
          ...updatedItem,
          ...(({ actualPrice, orderedQuantity, isDiscount, discountPercentage, discountedPrice, ...rest }) => rest)(
            updatedItem.item
          ),
        };
        delete updatedItem.item;
      }

      if (updatedItem.metaData) {
        updatedItem = {
          ...updatedItem,
          ...(({ actualPrice, orderedQuantity, isDiscount, discountPercentage, discountedPrice, ...rest }) => rest)(
            updatedItem.metaData
          ),
          cart_item_id: cartItemId,
        };

        delete updatedItem.metaData;
        delete updatedItem.item;
      }

      cart.items[index] = updatedItem;
    }
  }

  if (!cart?.customer) {
    cart.customer = {
      firstName: store.getState().auth?.user?.fName || '',
      lastName: store.getState().auth?.user?.lName || '',
    };
  }

  if (cart?.restaurant) {
    cart.resturantAddress = cart.restaurant;
    delete cart.restaurant;
  }
  if (!cart.location) {
    cart.location = {
      latitude: store.getState().auth?.user?.userLatitude,
      longitude: store.getState().auth?.user?.userLongitude,
    };
  }

  return cart;
};

export const getCartLocal = () => {
  const restaurantId = store.getState().restaurant.selectedBusinessId;
  const localData = cloneDeep(store.getState()?.cart?.orderingTime);
  return localData && restaurantId ? localData?.[restaurantId]?.cart : null;
};

export const saveCartLocal = (updatedState: any) => {
  const restaurantId = store.getState().restaurant.selectedBusinessId;
  let localData: any = cloneDeep(store.getState()?.cart?.orderingTime);
  if (!localData && restaurantId) {
    localData = {
      [restaurantId]: {
        cart: {},
      },
    };
  } else if (restaurantId && localData?.[restaurantId]) localData[restaurantId].cart = updatedState;
  store.dispatch(updateOrderingTime({ orderingTime: { ...localData } }));
};

export const removeCartLocal = () => {
  const restaurantId = store.getState().restaurant.selectedBusinessId;
  const localData: any = cloneDeep(store.getState()?.cart?.orderingTime);
  deleteCookie('scheduleOrder');
  deleteCookie('scheduledDate');
  deleteCookie('scheduledSlot');
  store.dispatch(updateCartSettings({ selectedScheduleType: 'ASAP' }));

  if (restaurantId && localData?.[restaurantId]?.cart) {
    delete localData?.[restaurantId]?.cart;
  }
  store.dispatch(updateOrderingTime({ orderingTime: { ...localData } }));
};

export const getGiftCardFields = (cart: Cart) => {
  if (cart.is_gift_card_applied) {
    return {
      is_gift_card_applied: cart?.is_gift_card_applied,
      user_gift_card_id: cart?.user_gift_card_id,
      giftCardAmount: cart?.giftCardAmount,
      giftCard: cart?.giftCard,
    };
  }
  return {};
};
